import { Component, Input, OnInit } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import IResource from '../../../_interfaces/IResource';
import IRole from '../../../_interfaces/IRole';
import { ErrorsService } from '../../../_services/errors/errors.service';
import { ResourcesService } from '../../../_services/resources/resources.service';
import { RolesService } from '../../../_services/roles/roles.service';
import { BaseComponent } from '../../../components/base/base.component';
import { SessionService } from '../../../_services/session/session.service';
import { InputSwitchModule } from 'primeng/inputswitch';
import { FormsModule } from '@angular/forms';
import { ResourceLabelPipe } from '../../../_pipes/resource-label.pipe';

type AdministrationSubgroupCode =
  | 'ACCESS'
  | 'USERS'
  | 'TREATMENTS'
  | 'CLINICS'
  | 'ROLES'
  | 'OTHER';

type AdministrationSubgroup = {
  code: AdministrationSubgroupCode;
  resources: IResource[];
};

type PermissionDomainGroup = {
  code: string;
  resources: IResource[];
  subgroups?: AdministrationSubgroup[];
};

@Component({
  standalone: true,
  selector: 'app-form-role-permissions',
  templateUrl: './form-role-permissions.component.html',
  styleUrls: ['./form-role-permissions.component.scss'],
  imports: [
    InputSwitchModule,
    TranslatePipe,
    FormsModule,
    ResourceLabelPipe,
  ],
})
export class FormRolePermissionsComponent
  extends BaseComponent
  implements OnInit {
  private readonly administrationGroupCode = 'ADMINISTRATION';
  private readonly adminAccessPermissionName = 'ADMIN_PANEL.ACCESS';
  private readonly permissionDomainOrder = [
    'APPOINTMENTS',
    'CALENDAR_NOTES',
    'PATIENTS',
    'DOCUMENTS',
    'SALES',
    'PAYMENTS',
    'TASKS',
    'STATISTICS',
    'ADMINISTRATION',
    'OTHER',
  ];
  private readonly administrationSubgroupOrder: AdministrationSubgroupCode[] = [
    'ACCESS',
    'USERS',
    'TREATMENTS',
    'CLINICS',
    'ROLES',
    'OTHER',
  ];

  @Input('payload')
  role: IRole;

  resources: IResource[] = [];
  groupedResources: PermissionDomainGroup[] = [];
  permissionsStatus: Record<number, Record<string, boolean>> = {};

  get isLockedAdminRole(): boolean {
    const normalizedName = `${this.role?.name || ''}`.trim().toLowerCase();
    return normalizedName === 'admin' || normalizedName === 'administrador';
  }

  constructor(
    public confirmService: ConfirmationService,
    public resourcesService: ResourcesService,
    public rolesService: RolesService,
    public messageService: MessageService,
    public translate: TranslateService,
    public errorsService: ErrorsService,
    public session: SessionService
  ) {
    super(session);
  }

  private getDomainOrder(domainCode: string): number {
    const idx = this.permissionDomainOrder.indexOf(domainCode);
    return idx >= 0 ? idx : this.permissionDomainOrder.length;
  }

  private normalizeDomainCode(resource: IResource): string {
    const explicitCode = `${resource?.domainCode || ''}`.trim().toUpperCase();
    if (explicitCode) {
      return explicitCode;
    }

    const fallback = `${resource?.name || ''}`
      .trim()
      .toUpperCase()
      .split('.')[0];
    return fallback || 'OTHER';
  }

  private normalizeResourceName(resource: IResource): string {
    return `${resource?.name || ''}`.trim().toUpperCase();
  }

  private getActionCode(resource: IResource): string {
    const explicitActionCode = `${resource?.actionCode || ''}`
      .trim()
      .toUpperCase();
    if (explicitActionCode) {
      return explicitActionCode;
    }

    const chunks = this.normalizeResourceName(resource).split('.');
    return chunks[chunks.length - 1] || '';
  }

  private isAdminAccessResource(resource: IResource): boolean {
    return this.normalizeResourceName(resource) === this.adminAccessPermissionName;
  }

  private isAdministrationManagedResource(resource: IResource): boolean {
    const resourceName = this.normalizeResourceName(resource);

    if (this.isAdminAccessResource(resource)) {
      return true;
    }

    return (
      resourceName.startsWith('SETTINGS.') || resourceName.startsWith('TREATMENTS.')
    );
  }

  private resolveDisplayDomainCode(resource: IResource): string {
    if (this.isAdministrationManagedResource(resource)) {
      return this.administrationGroupCode;
    }

    return this.normalizeDomainCode(resource);
  }

  private getAdministrationSubgroupCode(
    resource: IResource
  ): AdministrationSubgroupCode {
    const resourceName = this.normalizeResourceName(resource);

    if (resourceName === this.adminAccessPermissionName) {
      return 'ACCESS';
    }

    if (resourceName.startsWith('SETTINGS.USERS.')) {
      return 'USERS';
    }

    if (resourceName.startsWith('SETTINGS.ROLES.')) {
      return 'ROLES';
    }

    if (resourceName.startsWith('SETTINGS.CLINICS.')) {
      return 'CLINICS';
    }

    if (resourceName.startsWith('TREATMENTS.')) {
      return 'TREATMENTS';
    }

    return 'OTHER';
  }

  private buildAdministrationSubgroups(
    resources: IResource[]
  ): AdministrationSubgroup[] {
    const grouped = new Map<AdministrationSubgroupCode, IResource[]>();

    for (const resource of resources) {
      const subgroupCode = this.getAdministrationSubgroupCode(resource);
      const list = grouped.get(subgroupCode) || [];
      list.push(resource);
      grouped.set(subgroupCode, list);
    }

    return Array.from(grouped.entries())
      .map(([code, subgroupResources]) => ({
        code,
        resources: subgroupResources,
      }))
      .sort((a, b) => {
        const orderDiff =
          this.administrationSubgroupOrder.indexOf(a.code) -
          this.administrationSubgroupOrder.indexOf(b.code);

        if (orderDiff !== 0) {
          return orderDiff;
        }

        return a.code.localeCompare(b.code);
      });
  }

  private humanizeDomainCode(domainCode: string): string {
    return `${domainCode || ''}`
      .split('_')
      .filter((part) => !!part)
      .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
      .join(' ');
  }

  private buildDomainGroups(resources: IResource[]): PermissionDomainGroup[] {
    const grouped = new Map<string, IResource[]>();

    for (const resource of resources) {
      const domainCode = this.resolveDisplayDomainCode(resource);
      const list = grouped.get(domainCode) || [];
      list.push(resource);
      grouped.set(domainCode, list);
    }

    return Array.from(grouped.entries())
      .map(([code, domainResources]) => {
        const group: PermissionDomainGroup = {
          code,
          resources: domainResources,
        };

        if (code === this.administrationGroupCode) {
          group.subgroups = this.buildAdministrationSubgroups(domainResources);
        }

        return group;
      })
      .sort((a, b) => {
        const domainDiff = this.getDomainOrder(a.code) - this.getDomainOrder(b.code);
        if (domainDiff !== 0) {
          return domainDiff;
        }

        return a.code.localeCompare(b.code);
      });
  }

  get isAdminPanelAccessEnabled(): boolean {
    const adminAccessResource = this.resources.find((resource) =>
      this.isAdminAccessResource(resource)
    );

    if (!adminAccessResource) {
      return true;
    }

    return !!this.permissionsStatus[adminAccessResource.id]?.['value'];
  }

  isAdministrationGroup(group: PermissionDomainGroup): boolean {
    return group.code === this.administrationGroupCode;
  }

  getAdministrationSubgroups(
    group: PermissionDomainGroup
  ): AdministrationSubgroup[] {
    return group.subgroups || [];
  }

  getAdministrationSubgroupTitle(subgroupCode: AdministrationSubgroupCode): string {
    const key = `ADMIN.ROLES.DETAIL.ADMIN_SUBGROUPS.${subgroupCode}.TITLE`;
    const translated = this.translate.instant(key);
    if (translated && translated !== key) {
      return translated;
    }

    return this.humanizeDomainCode(subgroupCode);
  }

  getAdministrationSubgroupDescription(
    subgroupCode: AdministrationSubgroupCode
  ): string {
    const key = `ADMIN.ROLES.DETAIL.ADMIN_SUBGROUPS.${subgroupCode}.DESCRIPTION`;
    const translated = this.translate.instant(key);
    if (translated && translated !== key) {
      return translated;
    }

    return this.translate.instant(
      'ADMIN.ROLES.DETAIL.PERMISSION_DOMAINS.GENERIC_DESCRIPTION',
      {
        domain: this.humanizeDomainCode(subgroupCode),
      }
    );
  }

  isResourceSwitchDisabled(resource: IResource): boolean {
    if (this.isLockedAdminRole) {
      return true;
    }

    if (
      this.isAdministrationManagedResource(resource) &&
      !this.isAdminAccessResource(resource)
    ) {
      return !this.isAdminPanelAccessEnabled;
    }

    return false;
  }

  getDomainTitle(domainCode: string): string {
    const key = `ADMIN.ROLES.DETAIL.PERMISSION_DOMAINS.${domainCode}.TITLE`;
    const translated = this.translate.instant(key);
    if (translated && translated !== key) {
      return translated;
    }

    return this.humanizeDomainCode(domainCode);
  }

  getDomainDescription(domainCode: string): string {
    const key = `ADMIN.ROLES.DETAIL.PERMISSION_DOMAINS.${domainCode}.DESCRIPTION`;
    const translated = this.translate.instant(key);
    if (translated && translated !== key) {
      return translated;
    }

    return this.translate.instant(
      'ADMIN.ROLES.DETAIL.PERMISSION_DOMAINS.GENERIC_DESCRIPTION',
      {
        domain: this.humanizeDomainCode(domainCode),
      }
    );
  }

  private setPermissionValue(resourceId: number, value: boolean): void {
    const current = this.permissionsStatus[resourceId] || { value: false };
    this.permissionsStatus[resourceId] = {
      ...current,
      value,
    };
  }

  private showRolePermissionError(error: any): void {
    const code = this.errorsService.getMessageFromRoleError(error);
    this.messageService.add({
      summary: this.translate.instant('MESSAGES.ROLES.ERROR.SUMMARY'),
      detail: this.translate.instant(`MESSAGES.ROLES.ERROR.${code}`),
      severity: 'error',
    });
  }

  private persistPermission(
    resource: IResource,
    action: string,
    checked: boolean,
    options?: {
      emitSuccessToast?: boolean;
      emitErrorToast?: boolean;
      onSuccess?: () => void;
      onError?: (error: any) => void;
    }
  ): void {
    const emitSuccessToast = options?.emitSuccessToast !== false;
    const emitErrorToast = options?.emitErrorToast !== false;
    const status = checked ? 'ACTIVATED' : 'DEACTIVATED';

    this.rolesService
      .updatePermissions(
        this.customers_id,
        this.role.id,
        resource.id,
        action,
        checked
      )
      .subscribe({
        next: () => {
          this.setPermissionValue(resource.id, checked);

          if (emitSuccessToast) {
            this.messageService.clear();
            this.messageService.add({
              severity: 'success',
              detail: this.translate.instant(
                `MESSAGES.ROLES.PERMISSIONS.${status}.DETAIL`,
                {
                  role: this.role.name,
                  permission: this.translate.instant(
                    `COMMON.PERMISSIONS.${action.toUpperCase()}`
                  ),
                  resource: this.translate.instant(
                    `RESOURCES.${resource.name.toUpperCase()}.TITLE`
                  ),
                }
              ),
              summary: this.translate.instant(
                `MESSAGES.ROLES.PERMISSIONS.${status}.SUMMARY`
              ),
            });
          }

          options?.onSuccess?.();
        },
        error: (error) => {
          this.setPermissionValue(resource.id, !checked);

          if (emitErrorToast) {
            this.showRolePermissionError(error);
          }

          options?.onError?.(error);
        },
      });
  }

  private getDisablePriority(resource: IResource): number {
    const actionCode = this.getActionCode(resource);

    if (actionCode === 'READ') {
      return 100;
    }

    if (actionCode === 'ACCESS') {
      return 200;
    }

    return 10;
  }

  private disableResourcesSequentially(
    resources: IResource[],
    onComplete: () => void,
    onError: (error: any) => void
  ): void {
    if (!resources.length) {
      onComplete();
      return;
    }

    const [current, ...rest] = resources;

    this.persistPermission(current, 'value', false, {
      emitSuccessToast: false,
      emitErrorToast: false,
      onSuccess: () => {
        this.disableResourcesSequentially(rest, onComplete, onError);
      },
      onError,
    });
  }

  private disableAdministrationPermissions(
    accessResource: IResource,
    action: string
  ): void {
    const dependantResources = this.resources
      .filter(
        (resource) =>
          this.isAdministrationManagedResource(resource) &&
          !this.isAdminAccessResource(resource) &&
          !!this.permissionsStatus[resource.id]?.['value']
      )
      .sort((a, b) => this.getDisablePriority(a) - this.getDisablePriority(b));

    this.disableResourcesSequentially(
      dependantResources,
      () => {
        this.persistPermission(accessResource, action, false);
      },
      (error) => {
        this.showRolePermissionError(error);
        this.loadRole();
      }
    );
  }

  private confirmDisableAdministration(
    accessResource: IResource,
    action: string
  ): void {
    this.confirmService.confirm({
      header: this.translate.instant(
        'ADMIN.ROLES.DETAIL.ADMIN_PANEL_DISABLE_CONFIRM.TITLE'
      ),
      message: this.translate.instant(
        'ADMIN.ROLES.DETAIL.ADMIN_PANEL_DISABLE_CONFIRM.MESSAGE'
      ),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.disableAdministrationPermissions(accessResource, action);
      },
      reject: () => {
        this.setPermissionValue(accessResource.id, true);
      },
    });
  }

  updatePermission($event, resource: IResource, action: string) {
    if (this.isLockedAdminRole) {
      this.setPermissionValue(resource.id, true);
      return;
    }

    if (this.isResourceSwitchDisabled(resource)) {
      return;
    }

    const checked = !!$event?.checked;

    if (this.isAdminAccessResource(resource) && !checked) {
      this.setPermissionValue(resource.id, true);
      this.confirmDisableAdministration(resource, action);
      return;
    }

    this.persistPermission(resource, action, checked);
  }

  loadRole() {
    this.rolesService
      .get(this.customers_id, this.role.id)
      .subscribe((role: IRole) => {
        this.role = role;
        this.loadResources();
      });
  }

  loadResources() {
    this.resourcesService
      .getAll(this.customers_id)
      .subscribe((resources: IResource[]) => {
        this.resources = resources
          .sort((a, b) => {
            const aOrder = Number.isFinite(Number(a?.sortOrder))
              ? Number(a.sortOrder)
              : Number.MAX_SAFE_INTEGER;
            const bOrder = Number.isFinite(Number(b?.sortOrder))
              ? Number(b.sortOrder)
              : Number.MAX_SAFE_INTEGER;

            if (aOrder !== bOrder) {
              return aOrder - bOrder;
            }

            return a.name.localeCompare(b.name);
          });
        this.groupedResources = this.buildDomainGroups(this.resources);
        const resourceMap = new Map(
          (this.role.resources || []).map((r: any) => [r.resources_id, r])
        );
        this.resources.forEach((resource: IResource) => {
          const roleResource = resourceMap.get(resource.id);
          this.permissionsStatus[resource.id] = {
            value: this.isLockedAdminRole ? true : !!roleResource?.value,
          };
        });
      });
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.loadRole();
  }
}
